import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { parseIngredientLine, formatIngredient } from '@familyhub/shared';
import { Sheet } from '../platform/Sheet';
import { rdb, type RecipeRecord } from './db';
import { addRecipe, updateRecipe, deleteRecipe, type RecipeInput } from './actions';
import { storeRecipeImage } from './import';
import { ImageCropEditor } from './ImageCropEditor';
import {
  captureImage,
  captureImageFromUrl,
  type CapturedImage,
  type CropBox,
} from './capture';

// A dropdown sentinel that cannot collide with a real source name.
const NEW_SOURCE = ' new';

/**
 * The recipe editor — creates a new recipe (blank, or seeded from an import
 * draft), or edits an existing one; it is also the fallback when the AI importer
 * is not configured. Ingredients and steps are edited as plain lines (one per
 * row); an ingredient line is parsed best-effort into quantity/unit/name
 * (`parseIngredientLine`). Source fields (tags, url, images) come from the record
 * or the seed and are preserved untouched.
 */
export function RecipeSheet({
  recipe,
  seed,
  cropSource,
  userId,
  onClose,
  onSaved,
  onDeleted,
}: {
  recipe: RecipeRecord | null;
  /** Pre-fill a new recipe from an import draft. Ignored when editing. */
  seed?: RecipeInput;
  /**
   * The original uploaded photo behind an import draft's picture, with the box the
   * importer chose. Lets "Bijsnijden" re-crop the full screenshot rather than the
   * already-cropped result, so the user can widen the crop, not only tighten it.
   */
  cropSource?: { image: CapturedImage; box: CropBox | null } | null;
  userId: string | null;
  onClose: () => void;
  onSaved?: (id: string) => void;
  onDeleted?: () => void;
}) {
  const src: RecipeRecord | RecipeInput | null = recipe ?? seed ?? null;

  const [title, setTitle] = useState(src?.title ?? '');
  const [imagePath, setImagePath] = useState<string | null>(src?.image_path ?? null);
  const [cropping, setCropping] = useState<{ image: CapturedImage; box: CropBox | null } | null>(null);
  const [imgBusy, setImgBusy] = useState(false);
  const [imgError, setImgError] = useState('');
  const [sourceName, setSourceName] = useState(src?.source_name ?? '');
  // A source not already among the household's saved ones is entered by hand; the
  // dropdown otherwise picks an existing one. Held explicitly so choosing "new" and
  // then clearing the field does not snap back to the picker mid-edit.
  const [addingNewSource, setAddingNewSource] = useState(false);
  const [servings, setServings] = useState(String(src?.servings ?? 2));

  // The household's existing sources — pick one from the dropdown, or add a new one.
  const knownSources = useLiveQuery(async () => {
    const rows = await rdb.recepten.filter((r) => !r.deleted_at).toArray();
    return [...new Set(rows.map((r) => r.source_name?.trim()).filter((s): s is string => !!s))].sort(
      (a, b) => a.localeCompare(b, 'nl'),
    );
  }, []);
  const sources = useMemo(() => knownSources ?? [], [knownSources]);
  // Show the free-text field when adding a new source, when the household has none
  // yet, or when the current value is one not in the saved list (e.g. from import).
  const trimmedSource = sourceName.trim();
  const enterNewSource =
    addingNewSource ||
    sources.length === 0 ||
    (knownSources !== undefined && trimmedSource !== '' && !sources.includes(trimmedSource));
  const [minutes, setMinutes] = useState(src?.total_minutes != null ? String(src.total_minutes) : '');
  const [ingredients, setIngredients] = useState(
    (src?.ingredients ?? []).map((i) => formatIngredient(i)).join('\n'),
  );
  const [steps, setSteps] = useState((src?.steps ?? []).map((s) => s.text).join('\n'));
  const [notes, setNotes] = useState(src && 'notes' in src ? (src.notes ?? '') : '');

  const save = async () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    const input: RecipeInput = {
      title: trimmed,
      servings: Math.max(1, Number(servings) || 1),
      total_minutes: minutes.trim() ? Math.max(0, Number(minutes) || 0) : null,
      ingredients: ingredients
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map(parseIngredientLine),
      steps: steps
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((text) => ({ text })),
      tags: src?.tags ?? [],
      source_type: src?.source_type ?? 'manual',
      source_url: src?.source_url ?? null,
      source_name: sourceName.trim() || null,
      image_path: imagePath,
      source_images: src?.source_images ?? [],
      notes: notes.trim() || null,
    };
    if (recipe) {
      await updateRecipe(recipe.id, input);
      onClose();
    } else {
      const id = await addRecipe(input, userId);
      if (onSaved) onSaved(id);
      else onClose();
    }
  };

  const remove = async () => {
    if (recipe) await deleteRecipe(recipe.id);
    if (onDeleted) onDeleted();
    else onClose();
  };

  // Pick/take a new photo, then crop it. The whole image is the starting box.
  const pickPhoto = async (file: File | undefined) => {
    if (!file) return;
    setImgError('');
    setImgBusy(true);
    try {
      const image = await captureImage(file);
      setCropping({ image, box: null });
    } catch {
      setImgError('Kon de foto niet verwerken.');
    } finally {
      setImgBusy(false);
    }
  };

  // Re-crop the current picture: the original upload behind an import draft when we
  // have it (so the crop can widen), otherwise the stored picture reloaded.
  const recropCurrent = async () => {
    setImgError('');
    if (cropSource) {
      setCropping(cropSource);
      return;
    }
    if (!imagePath) return;
    setImgBusy(true);
    try {
      const image = await captureImageFromUrl(imagePath);
      setCropping({ image, box: null });
    } catch {
      setImgError('Kon de huidige foto niet laden om bij te snijden.');
    } finally {
      setImgBusy(false);
    }
  };

  // A finished crop from the editor: store it and use its path as the picture.
  const onCropped = async (cropped: CapturedImage) => {
    setImgBusy(true);
    try {
      setImagePath(await storeRecipeImage(cropped));
      setCropping(null);
    } catch {
      setImgError('Kon de foto niet opslaan.');
    } finally {
      setImgBusy(false);
    }
  };

  return (
    <Sheet title={recipe ? 'Recept bewerken' : 'Nieuw recept'} onClose={onClose}>
      <div className="sheet-field">
        <span className="sheet-label">Foto</span>
        {imagePath && <img className="recipe-photo-preview" src={imagePath} alt="" />}
        <div className="sheet-row recipe-photo-actions">
          {imagePath && (
            <button className="btn btn-outline" onClick={() => void recropCurrent()} disabled={imgBusy}>
              Bijsnijden
            </button>
          )}
          <label className="btn btn-outline">
            {imagePath ? 'Andere foto' : 'Foto toevoegen'}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                void pickPhoto(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
          {imagePath && (
            <button className="btn btn-quiet" onClick={() => { setImagePath(null); setImgError(''); }} disabled={imgBusy}>
              Verwijderen
            </button>
          )}
        </div>
        {imgError && <p className="sheet-note error">{imgError}</p>}
      </div>

      <label className="sheet-field">
        <span className="sheet-label">Titel</span>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Bijv. Risotto met asperges" />
      </label>

      <label className="sheet-field">
        <span className="sheet-label">Bron</span>
        {enterNewSource ? (
          <>
            <input
              type="text"
              value={sourceName}
              onChange={(e) => setSourceName(e.target.value)}
              placeholder="Bijv. Crisp, Nistisima of een vriend"
              autoFocus={addingNewSource}
            />
            {sources.length > 0 && (
              <button
                type="button"
                className="btn btn-quiet"
                style={{ alignSelf: 'flex-start', minHeight: 'auto', padding: '4px 8px' }}
                onClick={() => {
                  setAddingNewSource(false);
                  setSourceName('');
                }}
              >
                Kies uit lijst
              </button>
            )}
          </>
        ) : (
          <select
            value={sources.includes(trimmedSource) ? trimmedSource : ''}
            onChange={(e) => {
              if (e.target.value === NEW_SOURCE) {
                setAddingNewSource(true);
                setSourceName('');
              } else {
                setSourceName(e.target.value);
              }
            }}
          >
            <option value="">Geen bron</option>
            {sources.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
            <option value={NEW_SOURCE}>Nieuwe bron toevoegen…</option>
          </select>
        )}
      </label>

      <div className="sheet-row">
        <label className="sheet-field" style={{ flex: 1 }}>
          <span className="sheet-label">Porties</span>
          <input type="number" inputMode="numeric" min={1} value={servings} onChange={(e) => setServings(e.target.value)} />
        </label>
        <label className="sheet-field" style={{ flex: 1 }}>
          <span className="sheet-label">Minuten</span>
          <input type="number" inputMode="numeric" min={0} value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder="—" />
        </label>
      </div>

      <label className="sheet-field">
        <span className="sheet-label">Ingrediënten (één per regel)</span>
        <textarea
          rows={6}
          value={ingredients}
          onChange={(e) => setIngredients(e.target.value)}
          placeholder={'500 g spaghetti\n2 teentjes knoflook\nsnufje zout'}
        />
      </label>

      <label className="sheet-field">
        <span className="sheet-label">Stappen (één per regel)</span>
        <textarea
          rows={6}
          value={steps}
          onChange={(e) => setSteps(e.target.value)}
          placeholder={'Kook de pasta beetgaar.\nFruit de knoflook.'}
        />
      </label>

      <label className="sheet-field">
        <span className="sheet-label">Notities</span>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optioneel" />
      </label>

      <div className="sheet-row" style={{ marginTop: 4 }}>
        <button className="btn btn-primary" onClick={() => void save()} disabled={!title.trim()}>Opslaan</button>
        {recipe && <button className="btn btn-danger" onClick={() => void remove()}>Verwijderen</button>}
      </div>

      {cropping && (
        <ImageCropEditor
          image={cropping.image}
          initialBox={cropping.box}
          onCancel={() => setCropping(null)}
          onCropped={(c) => void onCropped(c)}
        />
      )}
    </Sheet>
  );
}
